import type { Direction } from "../../shared/trend-direction.js";

/**
 * 升降箭頭——監督者在揭曉日之前看到的「數字」。
 *
 * 方向由伺服器算好送來（死區的單位是公斤，前端沒有公斤），這裡只負責畫。
 *
 * `null` 是**沒有資料**，不是持平：兩者混掉的話畫面會把「還沒量」顯示成
 * 「沒有變化」，那是兩件完全不同的事。
 */

/**
 * 箭頭符號讀不出意義，所以每一個都要有文字標籤——徽章那邊已經立過同一個
 * 規矩（未達成的徽章不只靠顏色分辨）。
 */
const LABEL: Record<Direction, string> = { up: "上升", flat: "持平", down: "下降" };

/**
 * 手繪 SVG，不用 ↗ ↔ ↘ 這三個字符。
 *
 * 先前是字符，而它們的墨水面積比數字小很多、又常落在不支援粗體的後備字型
 * ——同樣的 font-size 下 `↘` 會比 `79.0 kg` 小一截，卡片的焦點就不見了。
 * 那時的解法是把字級放大到 1.35em 硬撐，治標。
 *
 * 畫成 SVG 之後粗細由 stroke-width 決定，跟分頁圖示同一個 24 方格、同一種
 * 圓端筆觸（見 src/ui/TabIcon.tsx），跟哪個平台裝了哪套字型無關。
 *
 * 尺寸用 `em` 而不是 px：跟著繼承來的字級走，手機版的斷點自動適用。樣式全部
 * 寫在行內，`main.css` 的選取器一個都不用動（鐵則 7）。
 */
const PATHS: Record<Direction, string> = {
  up: "M6.5 17.5 17.5 6.5M10 6.5h7.5V14",
  flat: "M4 12h16M8.5 7.5 4 12l4.5 4.5M15.5 7.5 20 12l-4.5 4.5",
  down: "M6.5 6.5 17.5 17.5M17.5 10v7.5H10"
};

const SIZE = { width: "1.25em", height: "1.25em", display: "block" };

export function Arrow({ direction }: { direction: Direction | null | undefined }) {
  if (!direction) {
    return <span style={{ fontSize: "1.35em", lineHeight: 1, fontWeight: 900 }} aria-label="尚未測量">—</span>;
  }
  return (
    <svg
      viewBox="0 0 24 24" role="img" aria-label={LABEL[direction]} style={SIZE}
      fill="none" stroke="currentColor" stroke-width="2.6"
      stroke-linecap="round" stroke-linejoin="round"
    >
      <path d={PATHS[direction]} />
    </svg>
  );
}
